import cn from 'classnames';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from 'components';
import { Button } from 'components/form';

import { API_ROUTES } from 'lib/api-routes';
import { ONBOARDING_FLOW_STATE } from 'lib/consts/flowState';
import { CompaniesResponse, CompanyAttributes } from 'lib/models/company';
import { TagAttributes, TagType } from 'lib/models/tag';
import { TUserApiResponse } from 'lib/models/User.model';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { apiInstance } from 'lib/utils/axios';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { getIndustryName } from 'utils/format';

import {
  CompanyCard,
  CompanyCardProps,
} from '../CompanyCard/CompanyCard.component';
import Header from '../Header';
import StepHeading from '../StepHeading';
import styles from './Step4.module.scss';

interface Step4Props {
  progress: number;
  setFlowState: (flowState: string) => void;
}

export const Step4: React.FC<Step4Props> = ({ progress, setFlowState }) => {
  const router = useRouter();
  const { recruiterCompany, updateUserApiResponse } = useAuth();
  const [similarCompanies, setSimilarCompanies] =
    useState<Array<CompanyAttributes> | null>(null);

  const [t] = useTranslation('onboarding.recruiter');

  useEffect(() => {
    if (recruiterCompany?.slug) {
      const similarCompaniesPathname = `${API_ROUTES.COMPANY_LIST}/${recruiterCompany.slug}${API_ROUTES.SIMILAR_STUDENT_SOCIETIES}?with_details=true&page=1&per_page=10`;
      apiInstance
        .get(similarCompaniesPathname)
        .then((res: { data: CompaniesResponse }) => {
          const list = parseArrayResponse<CompanyAttributes>(res.data);
          setSimilarCompanies(list);
        });
    }
  }, [recruiterCompany?.slug]);

  const handleNext = async () => {
    const flow_state = ONBOARDING_FLOW_STATE.POST_5;
    const recruiter = { flow_state };
    const userResponse = await apiInstance.patch<TUserApiResponse>(
      API_ROUTES.RECRUITER_SIGNUP,
      {
        recruiter,
      }
    );
    updateUserApiResponse(userResponse.data);
  };

  const getCards = useMemo(() => {
    if (similarCompanies === null) {
      return (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      );
    } else {
      return similarCompanies.map((company) => {
        const { industry: industryResponse, custom_industry_name } =
          company || {};
        const industryInfo =
          parseResponse<TagAttributes<TagType.Industry>>(industryResponse);
        const industry = getIndustryName(custom_industry_name, industryInfo);

        const companyProp: CompanyCardProps = {
          id: company.id,
          name: company.name,
          icon_url: company.icon_url,
          url: company.public_url || '',
          industry: industry,
          city: company.address.trim(),
          is_verified: company.is_verified,
          huzzle_verified: company.huzzle_verified,
        };
        return <CompanyCard {...companyProp} key={company.id} />;
      });
    }
  }, [similarCompanies]);

  return (
    <>
      <Header
        percentComplete={progress}
        onBack={() => {
          setFlowState(ONBOARDING_FLOW_STATE.POST_3);
        }}
      />
      <div className={styles.stepContent}>
        <StepHeading
          title={t('step-4.header.title')}
          desc={t('step-4.header.desc')}
          desc2={t('step-4.header.desc2')}
        />

        <div>
          <Button
            type="button"
            color="secondary"
            className={cn(styles.yesNoButton, styles.button)}
            onClick={() => {
              const win = window.open(
                `${PAGE_ROUTES.COMPANIES}/${recruiterCompany?.slug}`,
                '_blank'
              );
              win?.focus();
            }}
            isFullWidth
          >
            {t('step-4.view-profile')}
          </Button>
          <Button
            type="button"
            color="primary"
            className={styles.button}
            onClick={() => {
              router.push(`${PAGE_ROUTES.COMPANY_EDIT}?onboarding=true`);
            }}
            isFullWidth
          >
            {t('step-4.complete-profile')}
          </Button>
          <Button
            type="button"
            color="black"
            onClick={handleNext}
            variant="text"
            isFullWidth
            borderless
            className={styles.button}
          >
            {t('do-later')}
          </Button>
        </div>

        <div className={styles.divider}>
          <hr />
        </div>

        <StepHeading desc={t('step-4.header.desc-3')} />

        {getCards}
      </div>
    </>
  );
};
